import { Check, Clock, Pencil, XCircle } from 'lucide-react';
import { Badge } from '@ooh/ui/components/badge';
import { Button } from '@ooh/ui/components/button';
import type { ClientOfferReviewBannerModel } from './client-offer-review-banner-model';
import type {
  ClientOfferReviewDiffItem,
  ClientOfferReviewStatusBannerLabels,
} from './client-offer-review-types';

function BannerIcon({
  icon,
  className,
}: {
  icon: ClientOfferReviewBannerModel['icon'];
  className: string;
}) {
  if (icon === 'ok') return <Check className={`h-4 w-4 ${className}`} strokeWidth={2} />;
  if (icon === 'x') return <XCircle className={`h-4 w-4 ${className}`} strokeWidth={2} />;
  return <Clock className={`h-4 w-4 ${className}`} strokeWidth={2} />;
}

/** Top status banner + "co się zmieniło" diff panel (new-version-diff state only). */
export function ClientOfferReviewStatusBanner({
  banner,
  showDiff,
  prevVersion,
  diffExpanded,
  onToggleDiff,
  diffItems,
  labels,
}: {
  banner: ClientOfferReviewBannerModel;
  showDiff: boolean;
  prevVersion: number;
  diffExpanded: boolean;
  onToggleDiff: () => void;
  diffItems: ClientOfferReviewDiffItem[];
  labels: ClientOfferReviewStatusBannerLabels;
}) {
  const iconClassName =
    banner.icon === 'ok'
      ? 'text-emerald-600'
      : banner.icon === 'x'
        ? 'text-red-500'
        : 'text-amber-500';
  const diffTag = {
    price: { label: labels.diffTagPrice, variant: 'info' as const },
    removed: { label: labels.diffTagRemoved, variant: 'destructive' as const },
    added: { label: labels.diffTagAdded, variant: 'success' as const },
  } satisfies Record<
    ClientOfferReviewDiffItem['type'],
    { label: string; variant: 'info' | 'destructive' | 'success' }
  >;
  return (
    <>
      <div
        className={`flex shrink-0 items-center gap-2.5 border-b px-6 py-2.5 ${banner.frameClassName}`}
      >
        <BannerIcon icon={banner.icon} className={iconClassName} />
        <div className="flex flex-col">
          <span className={`text-sm font-semibold ${banner.titleClassName}`}>{banner.title}</span>
          {banner.desc != null ? (
            <span className={`text-xs ${banner.descClassName}`}>{banner.desc}</span>
          ) : null}
        </div>
      </div>

      {showDiff ? (
        <div
          data-testid="diff-panel"
          className="mx-6 mt-4 shrink-0 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800">
              <Pencil className="h-[15px] w-[15px]" strokeWidth={2} />
              {labels.diffTitle(prevVersion)}
            </span>
            <Button
              type="button"
              variant="link"
              size="xs"
              onClick={onToggleDiff}
              className="h-auto p-0 text-xs font-medium text-amber-700 underline"
            >
              {diffExpanded ? labels.diffCollapse : labels.diffShow(diffItems.length)}
            </Button>
          </div>
          {diffExpanded ? (
            <div className="mt-2.5 flex flex-col gap-1.5">
              {diffItems.map((d, i) => {
                const tag = diffTag[d.type];
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key -- static offer diff dataset, stable order
                    key={i}
                    className="flex items-baseline gap-2.5 text-xs"
                  >
                    <Badge
                      variant={tag.variant}
                      className="shrink-0 whitespace-nowrap px-2.5 py-px text-2xs"
                    >
                      {tag.label}
                    </Badge>
                    <span className="font-medium">{d.label}</span>
                    <span className="text-muted-foreground">{d.detail}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
